import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser } from 'react-icons/fi';
import Button from '../../components/UI/Button';

const AuthContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--primary-green) 0%,
    var(--secondary-blue) 100%
  );
  padding: var(--space-lg);
`;

const AuthCard = styled(motion.div)`
  background: white;
  border-radius: var(--radius-2xl);
  padding: var(--space-3xl);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 400px;
`;

const AuthHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-2xl);
  
  h1 {
    font-size: 2rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-sm);
  }
  
  p {
    color: var(--neutral-gray-600);
  }
`;

const AuthForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
`;

const InputGroup = styled.div`
  position: relative;
  
  input {
    width: 100%;
    padding: var(--space-md) var(--space-md) var(--space-md) 3rem;
    border: 1px solid var(--neutral-gray-300);
    border-radius: var(--radius-lg);
    font-size: 1rem;
    
    &:focus {
      border-color: var(--primary-green);
      box-shadow: 0 0 0 3px rgba(46, 139, 87, 0.1);
    }
    
    &.error {
      border-color: var(--accent-orange);
    }
  }
  
  .icon {
    position: absolute;
    left: var(--space-md);
    top: 50%;
    transform: translateY(-50%);
    color: var(--neutral-gray-400);
  }
  
  .toggle-password {
    position: absolute;
    right: var(--space-md);
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--neutral-gray-400);
    cursor: pointer;
    padding: 0;
    
    &:hover {
      color: var(--neutral-gray-600);
    }
  }
  
  .error-message {
    color: var(--accent-orange);
    font-size: 0.875rem;
    margin-top: var(--space-sm);
  }
`;

const AuthFooter = styled.div`
  margin-top: var(--space-xl);
  text-align: center;
  
  p {
    color: var(--neutral-gray-600);
    margin-bottom: var(--space-md);
  }
  
  a {
    color: var(--primary-green);
    font-weight: 500;
    
    &:hover {
      color: var(--primary-green-dark);
    }
  }
`;

const SocialLogin = styled.div`
  margin-top: var(--space-xl);
  
  .divider {
    display: flex;
    align-items: center;
    margin: var(--space-lg) 0;
    
    &::before,
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: var(--neutral-gray-300);
    }
    
    span {
      padding: 0 var(--space-md);
      color: var(--neutral-gray-500);
      font-size: 0.875rem;
    }
  }
`;

const SocialButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  padding: var(--space-md);
  border: 1px solid var(--neutral-gray-300);
  border-radius: var(--radius-lg);
  background: white;
  color: var(--neutral-gray-700);
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-base);
  margin-bottom: var(--space-md);
  
  &:hover {
    background: var(--neutral-gray-50);
    border-color: var(--neutral-gray-400);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock user data
      const userData = {
        id: 1,
        name: 'Luke Anderson',
        email: formData.email,
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
      };
      
      onLogin(userData);
      navigate('/dashboard');
    } catch (error) {
      setErrors({ submit: 'Login failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    console.log(`Login with ${provider}`);
    // Implement social login logic
  };

  return (
    <AuthContainer>
      <AuthCard
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <AuthHeader>
          <h1>Welcome Back!</h1>
          <p>Sign in to access premium content and upload your adventures</p>
        </AuthHeader>

        <AuthForm onSubmit={handleSubmit}>
          <InputGroup>
            <FiMail className="icon" size={20} />
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <div className="error-message">{errors.email}</div>}
          </InputGroup>

          <InputGroup>
            <FiLock className="icon" size={20} />
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
            {errors.password && <div className="error-message">{errors.password}</div>}
          </InputGroup>

          {errors.submit && (
            <div style={{ color: 'var(--accent-orange)', textAlign: 'center' }}>
              {errors.submit}
            </div>
          )}

          <Button type="submit" fullWidth disabled={isLoading}>
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </AuthForm>

        <SocialLogin>
          <div className="divider">
            <span>or continue with</span>
          </div>
          
          <SocialButton onClick={() => handleSocialLogin('google')}>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </SocialButton>
          
          <SocialButton onClick={() => handleSocialLogin('facebook')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Continue with Facebook
          </SocialButton>
        </SocialLogin>

        <AuthFooter>
          <p>Don't have an account?</p>
          <Link to="/register">Create an account</Link>
        </AuthFooter>
      </AuthCard>
    </AuthContainer>
  );
};

export default Login;
